"use client";

/**
 * The top strip: who you are with, and the chrome switches that are not
 * controls. Meet's arrangement, which is the point — chrome lives in a strip
 * ABOVE the video, never on top of it.
 *
 * This existed once, was deleted when the bottom bar went solid, and is back
 * for a different reason than it left. The old one was overlaid on the video
 * and needed a gradient behind it to stay readable; the video is now inset
 * between two strips, so there is nothing to read against and nothing to fade.
 */
import { cn } from "@repo/ui/lib/utils";
import type { ReactNode } from "react";
import { type CallStatus, useCall } from "./context";

export interface CallHeaderProps {
  /** Trailing chrome — `Call.Minimise` belongs here, at the strip's right. */
  children?: ReactNode;
  /**
   * A second line under the title, for a state the surface itself cannot name.
   * "Calling…" is the case it exists for: only the caller knows it placed the
   * call, so the word comes from whoever composed this rather than from here.
   */
  caption?: ReactNode;
  className?: string;
}

export function CallHeader({ children, caption, className }: CallHeaderProps) {
  const { title, status, geometry } = useCall();

  /**
   * MINIMISED, IT OVERLAYS — there is no strip to live in on a 320px card, so
   * it sits on the video itself behind a fade to transparent.
   *
   * A GRADIENT HERE, unlike anywhere else on the surface. A panel with a hard
   * bottom edge cuts the card in two and reads as a second element stuck on
   * top; a fade keeps it one object with a legible top. The expanded surface
   * has no gradients precisely because it does not need them: its chrome sits
   * in its own strips beside the video rather than on it.
   */
  if (geometry.minimised) {
    return (
      <div
        data-testid="call-header"
        className={cn(
          "absolute inset-x-0 top-0 z-10 rounded-t-2xl px-3.5 pb-8 pt-2.5 text-neutral-100",
          /*
           * EASED, NOT LINEAR, and every stop is earning its place.
           *
           * A two-stop fade has a CORNER at each end — the alpha is falling at
           * a constant rate and then abruptly is not — and the eye resolves a
           * change of slope as a line even though no edge exists (Mach
           * banding). A `via` stop makes it worse: it adds a third corner in
           * the middle, which is exactly where the line was showing.
           *
           * These stops trace alpha = 0.75·(1−t)², whose slope reaches zero at
           * the bottom, so the fade meets the video with nothing to resolve.
           *
           * Alpha-only, never `transparent`: `transparent` is a different
           * colour, and interpolating to it greys the fade on the way down.
           */
          "bg-[linear-gradient(to_bottom,rgb(0_0_0/0.75)_0%,rgb(0_0_0/0.61)_10%,rgb(0_0_0/0.48)_20%,rgb(0_0_0/0.37)_30%,rgb(0_0_0/0.27)_40%,rgb(0_0_0/0.19)_50%,rgb(0_0_0/0.12)_60%,rgb(0_0_0/0.07)_70%,rgb(0_0_0/0.03)_80%,rgb(0_0_0/0.01)_90%,rgb(0_0_0/0)_100%)]",
          className,
        )}
      >
        {title && <p className="truncate text-sm font-medium">{title}</p>}
        {caption}
        {children}
      </div>
    );
  }

  return (
    <div
      data-testid="call-header"
      className={cn("absolute inset-x-0 top-0 z-10 flex h-10 items-center gap-2 bg-muted px-3", className)}
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{title}</span>
      {caption}
      <StatusLabel status={status} />
      {children}
    </div>
  );
}

/**
 * Connecting, live, or gone. Silent while live: a label that is always there
 * says nothing when it matters, and "live" is the state you are looking at.
 */
function StatusLabel({ status }: { status: CallStatus }) {
  if (status === "live") return null;
  return (
    <span className="flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] text-muted-foreground">
      <span className="size-1.5 rounded-full bg-current" />
      {status === "connecting" ? "Connecting…" : "Ended"}
    </span>
  );
}
